/**
 * Единый источник ключей localStorage для прогресса прохождения теста.
 *
 * Формат: `test_${testId}_<bucket>`. Используется и при загрузке/сохранении
 * прогресса (testingSlice), и при его сбросе (Testing.tsx) — чтобы ключи не
 * разъезжались между местами. Раздельное вычисление этих строк в нескольких
 * файлах однажды уже привело к регрессии «Начать сначала» (сброс чистил ключи
 * с неактуальным testId).
 */
export interface TestStorageKeys {
    /** Ответы пользователя */
    answers: string;
    /** Лайки/дизлайки вопросов */
    likes: string;
    /** Тайминги ответов */
    durations: string;
    /** Валидность текущего вопроса */
    validity: string;
    /** id вопросов, заполненных авто-ответом (auto_answer) */
    autoFilled: string;
    /** Версия вопросов (для сброса прогресса при изменении теста) */
    revisionQuestions: string;
}

/**
 * Возвращает все ключи localStorage для указанного теста.
 *
 * @param testId — id теста; null/undefined допустимы, но дают ключи
 *                 вида `test_null_*` (сбрасывать в этом случае нечего).
 */
export function getTestStorageKeys(
    testId: number | string | null | undefined
): TestStorageKeys {
    return {
        answers: `test_${testId}_answers`,
        likes: `test_${testId}_likes`,
        durations: `test_${testId}_durations`,
        validity: `test_${testId}_validity`,
        autoFilled: `test_${testId}_autofilled`,
        revisionQuestions: `test_${testId}_revision_questions`,
    };
}

/**
 * Стирает прогресс прохождения из localStorage (ответы, лайки, тайминги, валидность,
 * авто-ответы) — те же ключи, что чистит «Начать сначала». Версию вопросов не трогает:
 * её сверяет и перезаписывает загрузка теста.
 */
export function clearTestProgress(testId: number | string | null | undefined): void {
    const keys = getTestStorageKeys(testId);
    [keys.answers, keys.likes, keys.durations, keys.validity, keys.autoFilled]
        .forEach((key) => localStorage.removeItem(key));
}
